import { createValidationSchema, isError, NotFoundError, posixDateField, stringField, uuidField, stringLiteralUnionField, uploadedFileField, ImageMimeType, } from "@for-it/domain-lib";
import { IdentityType, ApplicantEntity, MaritalStatus, } from "../../entities/applicant.js";
import { LocalityEntity } from "../../entities/locality.js";
import { ProvinceEntity } from "../../entities/province.js";
import { UserEntity } from "../../entities/user.js";
import { ApplicantNotFoundError } from "../../errors/applicant-not-found.js";
import { DniInUseError } from "../../errors/dni-in-use-error.js";
export const registerPersonalApplicantData = {
    name: "registerPersonalApplicantData",
    description: "saves an applicant's personal data",
    isAuthRequired: false,
    acceptsFiles: true,
    payloadSchema: createValidationSchema({
        id: uuidField({
            required: true,
            message: "Must be an UUID",
        }),
        firstName: stringField({
            required: true,
            message: "Must be a string",
        }),
        lastName: stringField({
            required: true,
            message: "Must be a string",
        }),
        phoneNumber: stringField({
            required: true,
            minLength: {
                value: 10,
                message: "The phone number must have at least 10 digits",
            },
            message: "Must be a string",
        }),
        identityType: stringLiteralUnionField({
            required: true,
            message: `Must be ${"DNI or PASSPORT"}`,
            allowedValues: Object.values(IdentityType),
        }),
        identityNumber: stringField({
            required: true,
            message: "Must be a string",
        }),
        dateOfBirth: posixDateField({
            required: true,
            message: "Must be a string",
        }),
        uploadedFile: uploadedFileField({
            required: false,
            message: "Must be an UploadedFile",
            acceptMimeTypes: [ImageMimeType],
        }),
        nationality: stringField({
            required: false,
            message: "Must be a string",
        }),
        address: stringField({
            required: false,
            message: "Must be a string",
        }),
        maritalStatus: stringLiteralUnionField({
            required: false,
            message: `Must be ${"MARRIED or SINGLE or DIVORCED or WIDOWED"}`,
            allowedValues: Object.values(MaritalStatus),
        }),
        gender: stringField({
            required: false,
            message: "Must be a string",
        }),
        provinceId: stringField({
            required: true,
            message: "Must be a valid uuid",
        }),
        localityId: stringField({
            required: true,
            message: "Must be a valid uuid",
        }),
    }),
    useCase: async ({ db, avatarFiles, time }, payload) => {
        const user = await db.findOne(UserEntity, {
            where: { id: payload.id },
        });
        if (!user)
            return new NotFoundError();
        const provincesId = await db.findOne(ProvinceEntity, {
            where: { name: payload.provinceId },
        });
        if (!provincesId)
            return new NotFoundError();
        const localitiesId = await db.findOne(LocalityEntity, {
            where: { name: payload.localityId },
        });
        if (!localitiesId)
            return new NotFoundError();
        let avatarId;
        if (payload.uploadedFile) {
            const result = await avatarFiles.store(payload.uploadedFile);
            if (isError(result))
                return result;
            avatarId = result.id;
        }
        const foundApplicantIdentityNumber = await db.findOneBy(ApplicantEntity, {
            identityNumber: payload.identityNumber,
        });
        if (foundApplicantIdentityNumber)
            return new DniInUseError();
        const applicant = await db.findOne(ApplicantEntity, {
            where: { userId: user.id },
        });
        if (!applicant)
            return new ApplicantNotFoundError();
        const now = time.now();
        const userData = {
            ...user,
            firstName: payload.firstName,
            lastName: payload.lastName,
            dateOfBirth: payload.dateOfBirth,
            phoneNumber: payload.phoneNumber,
            avatar: payload.uploadedFile ? avatarId : undefined,
            updatedAt: now,
            enabled: true,
        };
        const applicantData = {
            ...applicant,
            userId: payload.id,
            identityType: payload.identityType,
            identityNumber: payload.identityNumber,
            nationality: payload.nationality ?? undefined,
            address: payload.address ?? undefined,
            maritalStatus: payload.maritalStatus
                ? payload.maritalStatus
                : undefined,
            gender: payload.gender ?? undefined,
            provinceId: provincesId.id,
            localityId: localitiesId.id,
            createdAt: now,
            updatedAt: now,
        };
        await db.update(UserEntity, user.id, userData);
        await db.update(ApplicantEntity, applicant.id, applicantData);
        return {
            applicantId: applicant.id,
            role: user.role,
        };
    },
};
